// ── Enrichment provider catalog ───────────────────────────────────────────
// One declarative entry per data provider: capabilities, auth, endpoints, the
// raw fields it returns, and how each maps to OUR canonical schema (company
// attributes — see `updateCompanyProfileRequestSchema` in companies.ts — or
// person identity traits). This is the maintainable integration reference for
// current + future providers: adding/auditing a provider = editing one entry.
//
// `verified: true` means the contract was confirmed against the vendor's docs;
// `false` means it's an integration seam to confirm before enabling (never
// treat an unverified row as fact). `fieldMap[].to: null` flags a provider
// field we DON'T yet have a canonical slot for — a candidate to add.
//
// NB: this is documentation/contract today; the per-provider `fieldMap` is
// shaped so a normalizer can later be driven from it (raw → canonical) instead
// of hand-mapping in each provider.

export type EnrichmentCapability =
  | 'company'
  | 'person'
  | 'visitor'
  | 'intent'
  | 'web_search' // outbound web search for the research agents (not enrichment)
  | 'crm' // platform OAuth apps for CRM connections (not enrichment)
  | 'notification'; // platform OAuth apps for notification connections (not enrichment)

export type ProviderEndpoint = {
  capability: EnrichmentCapability;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  input: string; // what it takes: domain / email / hashed-email / ip / pixel …
  noMatch: string; // how a no-match is signalled
  status: 'wired' | 'planned' | 'future';
};

export type ProviderFieldMapping = {
  from: string; // raw field in the provider response
  to: string | null; // our canonical key, or null = no slot yet (candidate)
  type: 'string' | 'string[]' | 'number' | 'band';
  notes?: string;
};

// ── Provider credential schema ─────────────────────────────────────────────
// The runtime credentials/config a provider needs to actually run, stored in
// the shared `connections` vault (global '*' scope, super-admin managed)
// instead of env vars. This is the TEMPLATE: the super-admin UI renders a form
// from these field descriptors, and backend availability is "flag on + every
// required field present" — no per-provider branching.

// Provider slot ids (mirror backend ENRICHMENT_SLOTS / WEB_SEARCH_SLOT) a
// provider can serve. `web_search` is the research agents' outbound search slot.
export type EnrichmentSlot =
  | 'company_enrichment'
  | 'person_enrichment'
  | 'web_search'
  | 'visitor_company';

export type ProviderCredentialFieldKind =
  | 'secret' // encrypted at rest; never echoed back to the client (masked)
  | 'text' // plaintext config (e.g. an account id)
  | 'url'; // plaintext endpoint / base URL

export type ProviderCredentialField = {
  key: string; // settings key in the provider's stored credentials
  label: string;
  kind: ProviderCredentialFieldKind;
  // Gates availability. A field with a `default` is never `required` (the
  // default fills it), so only credentials/endpoints without a default gate.
  required?: boolean;
  default?: string; // fallback when unset (e.g. a fixed base URL)
  slot?: EnrichmentSlot; // when set, the field only applies to that slot
  help?: string;
  // The value is an endpoint URL. A secret URL (e.g. a webhook URL that works as
  // a bearer token) is still validated as a safe public endpoint.
  format?: 'url';
};

export type EnrichmentProviderSpec = {
  id: string;
  // Clean display name (just the brand). Longer context goes in `description`.
  name: string;
  // Short human description shown under the name on the provider card.
  description?: string;
  capabilities: EnrichmentCapability[];
  verified: boolean;
  docs?: string;
  baseUrl: string | null;
  auth: string; // header(s) / scheme, in words
  endpoints: ProviderEndpoint[];
  fieldMap: ProviderFieldMapping[];
  costBasis: string; // internal cost note (never customer-facing)
  // ── Runtime wiring (drives availability + the super-admin connector form) ──
  // `featureFlag` is this provider's DB kill-switch; absent = always on (the
  // internal provider). `slots` limits which enrichment slots it serves (absent
  // = all). `config` is the credentials/endpoints it needs (see ProviderCredentialField);
  // providers with a non-empty `config` appear in the Providers UI.
  featureFlag?: string;
  // Per-provider on/off toggle (stored on the connection, set on the Providers
  // page). Marks providers that moved off the feature-flag kill-switch.
  killSwitch?: boolean;
  slots?: EnrichmentSlot[];
  config?: ProviderCredentialField[];
  // When set, this provider is only available while the referenced slot has at
  // least one routable provider of its own (e.g. `web_research` draws on the
  // `web_search` waterfall). Keeps availability manifest-driven — no
  // per-provider branches in the backend.
  dependsOnSlot?: EnrichmentSlot;
};

export const ENRICHMENT_PROVIDERS: EnrichmentProviderSpec[] = [
  {
    id: 'internal',
    name: 'Internal',
    description: 'PDL Free Company Dataset seed + team curation',
    capabilities: ['company'],
    verified: true,
    docs: 'docs/ENRICHMENT.md',
    baseUrl: null, // local DB table `company_enrichment_source`
    auth: 'none (local lookup)',
    endpoints: [
      {
        capability: 'company',
        method: 'GET',
        path: 'company_enrichment_source (DB, by apex domain)',
        input: 'domain',
        noMatch: 'no row → null (no charge)',
        status: 'wired',
      },
    ],
    // PDL fields → canonical company attributes (mostly identity mapping).
    fieldMap: [
      { from: 'name', to: 'name', type: 'string' },
      { from: 'industry', to: 'industry', type: 'string' },
      {
        from: 'size',
        to: 'size',
        type: 'band',
        notes: 'PDL bucket == EMPLOYEE_BANDS',
      },
      { from: 'founded', to: 'founded', type: 'string' },
      { from: 'country', to: 'country', type: 'string' },
      { from: 'region', to: 'region', type: 'string' },
      { from: 'locality', to: 'locality', type: 'string', notes: 'city' },
      { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
      { from: 'twitter_url', to: 'twitter_url', type: 'string' },
      {
        from: 'website',
        to: 'website',
        type: 'string',
        notes: 'apex becomes the row key',
      },
    ],
    costBasis:
      '~$0 (no live call); seeded quarterly from the PDL CC BY 4.0 set',
  },
  {
    id: 'delivr',
    name: 'Delivr.ai',
    description: 'Person enrichment from an email the customer already holds',
    capabilities: ['person', 'intent'],
    verified: true,
    docs: 'https://docs.delivr.ai/reference',
    baseUrl: 'https://api.delivr.ai',
    auth: 'X-Api-Key: dlvr_… + X-Api-Secret: … (both required)',
    endpoints: [
      {
        capability: 'person',
        method: 'GET',
        path: '/api/v1/lookup?email=',
        input: 'email (or hem + hem_type)',
        noMatch: '404 (no charge)',
        status: 'wired',
      },
      {
        capability: 'intent',
        method: 'GET',
        path: '/api/v1/intent/company/{domain}, /api/v1/intent/{identifier}',
        input: 'domain / identifier / topic',
        noMatch: '404',
        status: 'future',
      },
    ],
    // Person lookup → identity traits (person slot, not company attributes).
    fieldMap: [
      {
        from: 'first_name',
        to: 'name',
        type: 'string',
        notes: 'name = first + last',
      },
      { from: 'last_name', to: 'name', type: 'string' },
      { from: 'current_business_email', to: 'email', type: 'string' },
      { from: 'business_emails', to: 'emails', type: 'string[]' },
      { from: 'company_name', to: 'company', type: 'string' },
      {
        from: 'is_email_business',
        to: null,
        type: 'string',
        notes: 'flag — no trait slot yet',
      },
    ],
    costBasis:
      '1 contact record per matched lookup (200); 404 free. DELIVR_PERSON_COST_USD is the contracted Contact Records rate at its first volume bracket. The anonymous pixel path bills the separate resolution_* meter.',
    killSwitch: true,
    // The wired email lookup only. Anonymous-visitor reveal was parked.
    slots: ['person_enrichment'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'X-Api-Key (dlvr_…)',
      },
      {
        key: 'apiSecret',
        label: 'API Secret',
        kind: 'secret',
        required: true,
        help: 'X-Api-Secret (paired with the key)',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.delivr.ai',
      },
    ],
  },
  {
    id: 'ipapi_is',
    name: 'ipapi.is',
    description: 'Reverse-IP company identification (public, PAYG)',
    capabilities: ['visitor'],
    verified: true, // endpoint + response shape verified (ipapi.is/developers.html)
    docs: 'https://ipapi.is/developers.html',
    baseUrl: 'https://api.ipapi.is',
    auth: 'key query param (?key=)',
    endpoints: [
      {
        capability: 'visitor',
        method: 'GET',
        path: '?q=<ip>&key=',
        input: 'ip',
        noMatch: 'no company.domain in the response',
        status: 'wired',
      },
    ],
    fieldMap: [
      { from: 'company.name', to: 'name', type: 'string' },
      { from: 'company.domain', to: 'domain', type: 'string' },
      {
        from: 'company.type',
        to: 'type',
        type: 'string',
        notes: 'business|isp|hosting|education|government|banking',
      },
    ],
    costBasis:
      'Per-lookup PAYG (~$0.0001–0.0002). $/lookup TBD from portal — IPAPI_IS cost 0 until confirmed.',
    // No featureFlag: gated by the `visitorIdCompany` capability + credential presence.
    slots: ['visitor_company'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'ipapi.is API key (passed as ?key=)',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.ipapi.is',
      },
    ],
  },
  {
    id: 'ipregistry',
    name: 'Ipregistry',
    description: 'Reverse-IP company identification (public, prepaid PAYG)',
    capabilities: ['visitor'],
    verified: true, // endpoint + response shape verified (ipregistry.co/docs)
    docs: 'https://ipregistry.co/docs/responses/ip-address-fields',
    baseUrl: 'https://api.ipregistry.co',
    auth: 'key query param (?key=)',
    endpoints: [
      {
        capability: 'visitor',
        method: 'GET',
        path: '/<ip>?key=',
        input: 'ip',
        noMatch: 'no company.domain in the response',
        status: 'wired',
      },
    ],
    fieldMap: [
      { from: 'company.name', to: 'name', type: 'string' },
      { from: 'company.domain', to: 'domain', type: 'string' },
      {
        from: 'company.type',
        to: 'type',
        type: 'string',
        notes: 'business|education|government|hosting|isp',
      },
    ],
    costBasis:
      'Prepaid credits (~$0.0001/lookup at 1M). $/lookup TBD from portal — IPREGISTRY cost 0 until confirmed.',
    slots: ['visitor_company'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'ipregistry API key (passed as ?key=)',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.ipregistry.co',
      },
    ],
  },
  {
    id: 'cleanlist',
    name: 'Cleanlist',
    description: 'Company enrichment REST, synchronous',
    capabilities: ['company'],
    verified: true,
    docs: 'https://docs.cleanlist.ai/api-reference/enrichment',
    baseUrl: 'https://api.cleanlist.ai',
    auth: 'Authorization: Bearer clapi_…',
    endpoints: [
      {
        capability: 'company',
        method: 'POST',
        path: '/api/v1/public/enrichment/company',
        input: 'domain (body)',
        noMatch:
          'empty record / 404 → no charge; 402 = out of credits (hard error)',
        status: 'wired',
      },
    ],
    fieldMap: [
      { from: 'name', to: 'name', type: 'string' },
      { from: 'industry', to: 'industry', type: 'string' },
      {
        from: 'industries',
        to: null,
        type: 'string[]',
        notes: 'candidate canonical: industries[]',
      },
      { from: 'employee_count_range', to: 'size', type: 'band' },
      {
        from: 'revenue_range',
        to: null,
        type: 'band',
        notes: 'candidate canonical: revenue band',
      },
      {
        from: 'hq_location',
        to: 'locality',
        type: 'string',
        notes: 'split into country/region/locality [VERIFY]',
      },
      { from: 'funding_stage', to: 'funding_stage', type: 'string' },
      {
        from: 'total_funding_usd',
        to: null,
        type: 'number',
        notes: 'candidate canonical: total_funding',
      },
      { from: 'tech_stack', to: 'tech_stack', type: 'string[]' },
      { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
      { from: 'twitter_url', to: 'twitter_url', type: 'string' },
    ],
    costBasis:
      '~$0.05/company (1 Cleanlist credit) [your estimate — confirm from contract]',
    killSwitch: true,
    slots: ['company_enrichment'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'Authorization: Bearer clapi_…',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.cleanlist.ai',
      },
    ],
  },
  {
    id: 'treg',
    name: 'treg',
    description: 'Routed company and person enrichment across many data providers',
    capabilities: ['company', 'person'],
    verified: true,
    docs: 'https://treg.to/catalog/companies/enrich',
    baseUrl: 'https://treg.to',
    auth: 'X-Treg-Token (+ X-Treg-Org for a personal token)',
    endpoints: [
      {
        capability: 'company',
        method: 'POST',
        path: '/call/treg.companies.enrich',
        input: 'domain (body)',
        noMatch:
          '200 with output.name null (_treg.outcome miss), may be billed; 429/5xx = no-match, other 4xx = hard error',
        status: 'wired',
      },
      {
        capability: 'person',
        method: 'POST',
        path: '/call/treg.people.enrich',
        input: 'email (body)',
        noMatch:
          '200 with output.full_name null (_treg.outcome miss), may be billed; 429/5xx = no-match, other 4xx = hard error',
        status: 'wired',
      },
    ],
    fieldMap: [
      { from: 'name', to: 'name', type: 'string' },
      { from: 'industry', to: 'industry', type: 'string' },
      { from: 'employees', to: 'size', type: 'band' },
      {
        from: 'location',
        to: 'locality',
        type: 'string',
        notes: 'split into country/region/locality',
      },
      { from: 'founded', to: 'founded', type: 'number' },
      { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
      { from: 'website', to: 'website', type: 'string' },
      { from: 'description', to: 'description', type: 'string' },
      { from: 'full_name', to: 'name', type: 'string', notes: 'person' },
      { from: 'title', to: 'title', type: 'string', notes: 'person' },
      { from: 'company', to: 'company', type: 'string', notes: 'person' },
    ],
    // Per-success providers: company $0.000495 (BlitzAPI) to $0.25 (Lusha); person by email $0.000495 (BlitzAPI) to $0.28 (PDL).
    costBasis:
      'X-Treg-Cost-Micro per call; per-success misses are free, per-call/per-result misses bill',
    killSwitch: true,
    slots: ['company_enrichment', 'person_enrichment'],
    config: [
      {
        key: 'apiKey',
        label: 'Token',
        kind: 'secret',
        required: true,
        help: 'X-Treg-Token; a team token needs no org',
      },
      {
        key: 'org',
        label: 'Org Slug',
        kind: 'text',
        help: 'X-Treg-Org; only for a personal token',
      },
      {
        key: 'companyMaxCostUsd',
        label: 'Company Max Cost per Call (USD)',
        kind: 'text',
        default: '0.03',
        slot: 'company_enrichment',
        help: 'X-Treg-Route-Max-Cost: providers priced above this are skipped; never above the Company Lookup credit value',
      },
      {
        key: 'companyExcludeProviders',
        label: 'Company Excluded Providers',
        kind: 'text',
        // treg's routed companies.enrich children priced per call or per result, which bill a miss.
        default:
          'companyenrich,predictleads,contactout,dropleads,sumble,harvestapi',
        slot: 'company_enrichment',
        help: 'X-Treg-Route-Exclude, comma-separated treg provider names; empty uses the default',
      },
      {
        key: 'companyPreferProviders',
        label: 'Company Preferred Providers',
        kind: 'text',
        slot: 'company_enrichment',
        help: 'X-Treg-Route-Prefer, comma-separated; tried first, nothing else is excluded',
      },
      {
        key: 'personMaxCostUsd',
        label: 'Person Max Cost per Call (USD)',
        kind: 'text',
        default: '0.03',
        slot: 'person_enrichment',
        help: 'X-Treg-Route-Max-Cost: providers priced above this are skipped; never above the Person Lookup credit value',
      },
      {
        key: 'personExcludeProviders',
        label: 'Person Excluded Providers',
        kind: 'text',
        // treg's routed people.enrich children priced per call or per result, which bill a miss.
        default: 'lusha,harvestapi',
        slot: 'person_enrichment',
        help: 'X-Treg-Route-Exclude, comma-separated treg provider names; empty uses the default',
      },
      {
        key: 'personPreferProviders',
        label: 'Person Preferred Providers',
        kind: 'text',
        slot: 'person_enrichment',
        help: 'X-Treg-Route-Prefer, comma-separated; tried first, nothing else is excluded',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://treg.to',
      },
    ],
  },
  // ── Web search providers (research agents' outbound search waterfall) ──────
  // (Amazon Bedrock AgentCore Web Search is a planned addition — see
  // .ai/plans/agentcore-web-search/.)
  {
    id: 'brave',
    name: 'Brave Search',
    description: 'Independent web-search index API',
    // Contract confirmed 2026-06-27 against Brave's official API reference +
    // Brave's own MCP server types.
    capabilities: ['web_search'],
    verified: true,
    docs: 'https://api-dashboard.search.brave.com/api-reference/web/search/get',
    baseUrl: 'https://api.search.brave.com',
    auth: 'X-Subscription-Token header',
    endpoints: [
      {
        capability: 'web_search',
        method: 'GET',
        path: '/res/v1/web/search?q=',
        input: 'query',
        noMatch: 'empty web.results',
        status: 'planned',
      },
    ],
    fieldMap: [],
    costBasis: 'Brave Search API per-query — confirm plan/pricing',
    killSwitch: true,
    slots: ['web_search'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'X-Subscription-Token',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.search.brave.com',
      },
    ],
  },
  {
    id: 'tavily',
    name: 'Tavily Search',
    description: 'Web search API built for AI agents',
    // Contract confirmed 2026-06-28 against Tavily's official API reference.
    capabilities: ['web_search'],
    verified: true,
    docs: 'https://docs.tavily.com/documentation/api-reference/endpoint/search',
    baseUrl: 'https://api.tavily.com',
    auth: 'Authorization: Bearer header',
    endpoints: [
      {
        capability: 'web_search',
        method: 'POST',
        path: '/search',
        input: 'query',
        noMatch: 'empty results',
        status: 'planned',
      },
    ],
    fieldMap: [],
    costBasis: 'Tavily Search API per-query — confirm plan/pricing',
    killSwitch: true,
    slots: ['web_search'],
    config: [
      {
        key: 'apiKey',
        label: 'API Key',
        kind: 'secret',
        required: true,
        help: 'Authorization: Bearer tvly-…',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.tavily.com',
      },
    ],
  },
  {
    id: 'dataforseo',
    name: 'DataForSEO',
    description: 'Structured SERP + SEO data API (primary web-search provider)',
    // Contract confirmed 2026-07-10 against DataForSEO's official docs:
    //   • auth: https://docs.dataforseo.com/v3/auth/ (HTTP Basic, base64(login:password))
    //   • SERP: https://docs.dataforseo.com/v3/serp/google/organic/live/advanced/
    capabilities: ['web_search'],
    verified: true,
    docs: 'https://docs.dataforseo.com/v3/serp/google/organic/live/advanced/',
    baseUrl: 'https://api.dataforseo.com',
    auth: 'Authorization: Basic base64(login:password)',
    endpoints: [
      {
        capability: 'web_search',
        method: 'POST',
        path: '/v3/serp/google/organic/live/advanced',
        input: 'query',
        noMatch: 'empty tasks[].result[].items',
        status: 'planned',
      },
    ],
    fieldMap: [],
    costBasis:
      'DataForSEO Live Advanced SERP $0.002/query (Standard queue $0.0006) — pay-as-you-go',
    killSwitch: true,
    slots: ['web_search'],
    // Basic Auth needs TWO fields: the account API login (an identifier — plaintext
    // `text`) and the auto-generated API password (secret → vault). Both required.
    config: [
      {
        key: 'login',
        label: 'API Login',
        kind: 'text',
        required: true,
        help: 'DataForSEO account API login (email), from the API Access tab',
      },
      {
        key: 'password',
        label: 'API Password',
        kind: 'secret',
        required: true,
        help: 'Auto-generated API password (API Access tab) — NOT your account password',
      },
      {
        key: 'baseUrl',
        label: 'Base URL',
        kind: 'url',
        default: 'https://api.dataforseo.com',
      },
    ],
  },
  {
    id: 'web_research',
    name: 'Web Research',
    description:
      'Web search + extraction model — the last-resort gap-filler when data providers no-match',
    capabilities: ['company'],
    // Internal composition (our own search waterfall + Bedrock extraction) —
    // there is no external contract to verify.
    verified: true,
    docs: 'docs/ENRICHMENT.md',
    baseUrl: null,
    auth: 'none (internal composition: web_search slot + Bedrock)',
    endpoints: [
      {
        capability: 'company',
        method: 'GET',
        path: 'WebSearchService.search → Bedrock extraction (in-process)',
        input: 'domain',
        noMatch:
          'no search results, or extraction yields no core field → null (no charge)',
        status: 'wired',
      },
    ],
    // The extraction contract: the model may emit ONLY these flat fields, all
    // of which normalizeAttributes already canonicalizes.
    fieldMap: [
      { from: 'name', to: 'name', type: 'string' },
      { from: 'description', to: 'description', type: 'string' },
      { from: 'industry', to: 'industry', type: 'string' },
      {
        from: 'employee_count',
        to: 'size',
        type: 'number',
        notes: 'via normalizeAttributes EMPLOYEE_BANDS',
      },
      {
        from: 'hq_location',
        to: 'locality',
        type: 'string',
        notes: 'normalizer splits into locality/region/country',
      },
      { from: 'founded', to: 'founded', type: 'string' },
      { from: 'company_type', to: 'company_type', type: 'string' },
      { from: 'funding_stage', to: 'funding_stage', type: 'string' },
      { from: 'revenue_range', to: 'revenue_range', type: 'string' },
      { from: 'linkedin_url', to: 'linkedin_url', type: 'string' },
    ],
    costBasis:
      'extraction-model tokens (computed per run from MODEL_USD_RATES) + one web-search query (search per-query cost not modeled yet)',
    featureFlag: 'webResearch',
    slots: ['company_enrichment'],
    dependsOnSlot: 'web_search',
  },
  {
    // The OAuth app customers install when they connect HubSpot. Not a data
    // provider: `slots: []` keeps it out of every enrichment waterfall.
    id: 'hubspot_app',
    name: 'HubSpot',
    description: 'OAuth app customers install when they connect HubSpot',
    capabilities: ['crm'],
    verified: true,
    docs: 'https://developers.hubspot.com/docs/apps/developer-platform/build-apps/authentication/oauth/working-with-oauth',
    baseUrl: 'https://api.hubapi.com',
    auth: 'OAuth 2.0 authorization code; per-account Bearer tokens from /oauth/2026-09/token',
    endpoints: [],
    fieldMap: [],
    costBasis: 'no per-call cost (customer-installed OAuth app)',
    slots: [],
    config: [
      {
        key: 'clientId',
        label: 'Client ID',
        kind: 'text',
        required: true,
        help: "From the app's Auth page in HubSpot",
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        kind: 'secret',
        required: true,
        help: "From the app's Auth page. Rotating it in HubSpot stops the old one working",
      },
    ],
  },
  {
    // The Slack app customers install when they connect Slack. Not a data
    // provider: `slots: []` keeps it out of every enrichment waterfall.
    id: 'slack_app',
    name: 'Slack',
    description: 'OAuth app customers install when they connect Slack',
    capabilities: ['notification'],
    verified: true,
    docs: 'https://docs.slack.dev/authentication/installing-with-oauth',
    baseUrl: 'https://slack.com/api',
    auth: 'OAuth 2.0 authorization code; per-workspace bot tokens from oauth.v2.access',
    endpoints: [],
    fieldMap: [],
    costBasis: 'no per-call cost (customer-installed OAuth app)',
    slots: [],
    config: [
      {
        key: 'clientId',
        label: 'Client ID',
        kind: 'text',
        required: true,
        help: "From the app's App Management page in Slack",
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        kind: 'secret',
        required: true,
        help: 'Exchanges install codes for bot tokens and uninstalls the app on disconnect',
      },
      {
        key: 'signingSecret',
        label: 'Signing Secret',
        kind: 'secret',
        required: false,
        help: "Under Basic Info in the app's settings. Verifies requests Slack sends to Logspot",
      },
    ],
  },
  {
    // The OAuth app customers install when they connect Attio. Not a data
    // provider: `slots: []` keeps it out of every enrichment waterfall.
    id: 'attio_app',
    name: 'Attio',
    description: 'OAuth app customers install when they connect Attio',
    capabilities: ['crm'],
    verified: true,
    docs: 'https://docs.attio.com/rest-api/tutorials/connect-an-app-through-oauth',
    baseUrl: 'https://api.attio.com',
    auth: 'OAuth 2.0 authorization code; per-workspace Bearer tokens from app.attio.com/oauth/token',
    endpoints: [],
    fieldMap: [],
    costBasis: 'no per-call cost (customer-installed OAuth app)',
    slots: [],
    config: [
      {
        key: 'clientId',
        label: 'Client ID',
        kind: 'text',
        required: true,
        help: "From the app's OAuth tab at build.attio.com",
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        kind: 'secret',
        required: true,
        help: "From the app's OAuth tab at build.attio.com",
      },
    ],
  },
  {
    // The External Client App customers authorize when they connect Salesforce.
    // Not a data provider: `slots: []` keeps it out of every enrichment waterfall.
    id: 'salesforce_app',
    name: 'Salesforce',
    description:
      'External Client App customers authorize when they connect Salesforce',
    capabilities: ['crm'],
    verified: true,
    docs: 'https://help.salesforce.com/s/articleView?id=xcloud.external_client_apps.htm',
    baseUrl: 'https://login.salesforce.com',
    auth: 'OAuth 2.0 authorization code with PKCE; per-org refresh tokens, rotated on every refresh',
    endpoints: [],
    fieldMap: [],
    costBasis: 'no per-call cost (customer-installed managed package)',
    slots: [],
    config: [
      {
        key: 'clientId',
        label: 'Consumer Key',
        kind: 'text',
        required: true,
        help: 'External Client App Manager → Logspot → Settings → OAuth Settings → Consumer Key and Secret',
      },
      {
        key: 'clientSecret',
        label: 'Consumer Secret',
        kind: 'secret',
        required: true,
        help: 'Shown next to the Consumer Key. Never share it, including with Salesforce reviewers',
      },
      {
        key: 'fieldNamespace',
        label: 'Field Namespace',
        kind: 'text',
        required: false,
        help: 'Leave blank to use the Logspot package (logspot). Enter none only for a test org with the fields deployed outside the package',
      },
    ],
  },
];

/** Lookup a provider spec by id. */
export const getEnrichmentProvider = (
  id: string,
): EnrichmentProviderSpec | undefined =>
  ENRICHMENT_PROVIDERS.find((p) => p.id === id);

/** Providers that expose runtime config (credentials/endpoints) — i.e. the ones
 *  the super-admin Providers UI renders a credential form for. */
export const CREDENTIAL_PROVIDERS: EnrichmentProviderSpec[] =
  ENRICHMENT_PROVIDERS.filter((p) => (p.config?.length ?? 0) > 0);

// ── Provider-credential wire DTOs (super-admin Providers API) ──────────────
export type ProviderCredentialSettingState = {
  set: boolean; // a value is stored
  value?: string; // present for non-secret fields only (secrets never echoed)
};

export type ProviderCredentialState = {
  provider: string;
  enabled: boolean; // the provider's on/off kill-switch is ON
  configured: boolean; // enabled + all required fields present (any slot)
  missing_required: string[]; // required field keys still missing a value
  settings: Record<string, ProviderCredentialSettingState>;
};

export type ProviderCredentialsResponse = {
  providers: ProviderCredentialState[];
};
